import { useCallback } from "react"
import { useDispatch } from "react-redux"
import { useTranslation } from "react-i18next"
import { styled } from "@mui/material/styles"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"
import { ITEM, ROLLBACK_TRUE_WISH } from "constants/index"
import { NAVIGATE_TO_OTHER_USER_PROFILE_PAGE } from "constants/events"
import { useAppSelector } from "appRedux/hooks"
import { updateShowGeniesList, updateShowRollbackConfirmationDialog } from "appRedux/slices/wishListSlice"
import useGetGenies from "hooks/useGetGenies"
import UndoIcon from "components/atoms/_icons/UndoIcon"
import Avatar from "components/atoms/Avatar"
import Avatars from "components/atoms/Avatars"
import withRoleBasedPermission from "components/HOCs/withRoleBasedPermission"

function GiftFromOneGenie(props: UserDto) {
  const { id, handle, avatar } = props

  const { t } = useTranslation()

  function handleGenieClicked() {
    const eventDetail = {
      id,
      handle
    }
    document.dispatchEvent(new CustomEvent(NAVIGATE_TO_OTHER_USER_PROFILE_PAGE, { detail: eventDetail }))
  }

  return <>
    <Stack direction="row" alignItems="center" justifyContent="center" onClick={handleGenieClicked}>
      <AvatarWrapper><Avatar imageSrc={avatar}/></AvatarWrapper>
      <Username>{handle}</Username>
    </Stack>
    <Text>{t("items.giftFromOneGenie")}</Text>
  </>
}

function GiftFromMoreThanOneGenie({ genies }: { genies: UserDto[]; }) {
  const { t } = useTranslation()

  return <>
    <Avatars type={"linear"} users={genies}/>
    <Text style={{ transform: `translateX(${4 * (genies?.length - 1)}px)` }}>{t("items.giftFromMoreThanOneGenie")}</Text>
  </>
}

export default function GeniesInfoSection() {
  const wishListState = useAppSelector((state) => state.wishList)
  const genies = useAppSelector((state) => (state.wishList.genies && wishListState?.itemInfo?.id) ? state.wishList.genies[wishListState?.itemInfo?.id] : [])
  const currentPageInfo = useAppSelector((state) => state.global.currentPageInfo)

  const { isFetching } = useGetGenies(wishListState?.itemInfo?.id, wishListState?.itemInfo?.cameTrue)

  const { t } = useTranslation()
  const dispatch = useDispatch()

  const renderGenies = useCallback(() => {
    if (!genies || !genies.length) return null
    if (genies.length === 1) {
      if (genieIsItemOwner(genies[0].handle)) {
        return <Text>{t("items.purchased")}</Text>
      } else {
        return <GiftFromOneGenie {...genies[0]}/>
      }
    } else {
      return <GiftFromMoreThanOneGenie genies={genies}/>
    }
  }, [wishListState?.itemInfo?.id, JSON.stringify(genies)])

  function genieIsItemOwner(handle: string) {
    return handle === currentPageInfo?.dynamicPathParts?.handle
  }

  function handleContainerClicked() {
    if (isFetching) return
    if (!genies || genies.length <= 1) return
    dispatch(updateShowGeniesList(true))
  }

  function handleRollbackButtonClicked() {
    dispatch(updateShowRollbackConfirmationDialog(true))
  }

  return (<>
    {genies?.length ? <Container>
      <RollbackWrapperWithRoleBasedPermission
        resource={ITEM}
        action={ROLLBACK_TRUE_WISH}
        onClick={handleRollbackButtonClicked}
      >
        <UndoIcon/>
      </RollbackWrapperWithRoleBasedPermission>
      <Stack direction="row" alignItems="center" justifyContent="center" onClick={handleContainerClicked}>
        {renderGenies()}
      </Stack>
    </Container> : null}
  </>)
}

const Container = styled(Stack)(({ theme }) => ({
  width: "fit-content",
  height: "40px",
  flexDirection: "row",
  justifyContent: "center",
  alignItems: "center",
  padding: "8px 4px",
  margin: "0 auto",
  borderRadius: "64px",
  border: `1px solid ${theme.palette.gray["13"]}`,
  color: "#FFFFFF",
  overflow: "hidden",
  position: "relative",

  "&::before": {
    content: "''",
    width: "100%",
    height: "100%",
    background: `linear-gradient(to right, ${theme.palette.gray["14"]}, ${theme.palette.gray["15"]})`,
    opacity: 0.7,
    position: "absolute",
    top: 0,
    left: 0,
    zIndex: -1
  }
}))

const AvatarWrapper = styled("div")(({ theme }) => ({
  width: "32px",
  height: "32px"
}))

const Text = styled(Typography)(({ theme }) => ({
  fontSize: "1.5rem",
  margin: "0 8px"
}))

const Username = styled(Typography)(({ theme }) => ({
  fontSize: "1.5rem",
  marginLeft: "8px"
}))

const RollbackWrapper = styled(Stack)(({ theme }) => ({
  height: "32px",
  flexDirection: "row",
  justifyContent: "center",
  alignItems: "center",
  padding: "12px",
  marginRight: "8px",
  borderRight: `1px solid ${theme.palette.gray["13"]}`,

  "& svg": {
    stroke: "#FFFFFF"
  }
}))

const RollbackWrapperWithRoleBasedPermission = withRoleBasedPermission(RollbackWrapper)
